import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Trophy, CheckCircle, Phone } from "lucide-react";
import { useState } from "react";
import SEOHead from "@/components/SEOHead";

const IMAGES = {
  hero: "/basketball-team.jpg",
  defense: "/basketball-defense.jpg",
  dribble: "/basketball-dribble.jpg",
  smile: "/basketball-smile.jpg",
};

const details = [
  { icon: Users, label: "Yaş Grubu", value: "6-14 Yaş" },
  { icon: Trophy, label: "Seviye", value: "Başlangıç - İleri" },
];

const highlights = [
  "Temel basketbol teknikleri (dribling, şut, pas)",
  "Bireysel ve takım taktikleri",
  "Mini basketbol (küçük yaş grupları)",
  "Turnuva ve lig müsabakaları",
  "Fiziksel ve mental gelişim programları",
  "Şut tekniği ve serbest atış çalışması",
  "Savunma ve hücum pozisyonları",
  "Kondisyon ve çeviklik antrenmanı",
];

const trainingProgram = [
  { level: "Başlangıç", ages: "6-8 Yaş", desc: "Temel top kontrolü, dribling, basit pas teknikleri ve eğlenceli mini oyunlarla basketbola giriş.", color: "bg-gold" },
  { level: "Gelişim", ages: "9-11 Yaş", desc: "Şut tekniği, lay-up, takım savunması, hücum düzenleri ve mini lig müsabakaları.", color: "bg-burgundy" },
  { level: "İleri", ages: "12-14 Yaş", desc: "İleri taktik çalışmalar, pozisyon bazlı eğitim, kondisyon programları ve turnuva katılımı.", color: "bg-[#1A1A1A]" },
];

const galleryItems = [
  { img: IMAGES.hero, title: "Takım Kadromuz", span: "md:col-span-2 md:row-span-2" },
  { img: IMAGES.dribble, title: "Dribling Çalışması" },
  { img: IMAGES.defense, title: "Savunma Antrenmanı" },
  { img: IMAGES.smile, title: "Sporcularımız", span: "md:col-span-2" },
];

export default function BasketballPage() {
  const [lightboxImg, setLightboxImg] = useState(null);

  return (
    <div data-testid="basketball-page" className="pt-20">
      <SEOHead
        title={"Basketbol Kursu Ataşehir, Kadıköy, Maltepe - Profesyonel Eğitim"}
        description={"İRÜ Spor Kulübü basketbol kursu. Ataşehir, Kadıköy, Maltepe bölgesinde profesyonel basketbol eğitimi. 6-14 yaş grubu, deneyimli antrenörler. Anadolu yakası basketbol spor okulu."}
        keywords={"basketbol kursu Kadıköy, basketbol eğitimi Ataşehir, basketbol spor okulu Maltepe, Anadolu yakası basketbol, çocuk basketbol kursu İstanbul, basketbol antrenmanı, mini basketbol, genç basketbol, Bostancı basketbol, basketbol okulu İstanbul"}
        canonical="/branslarimiz/basketbol"
        ogImage="/basketball-team.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Basketbol Kursu - İRÜ Spor Kulübü",
          "description": "Ataşehir, Kadıköy, Maltepe bölgesinde profesyonel basketbol eğitimi. 6-14 yaş grubu için deneyimli antrenörler eşliğinde basketbol kursu.",
          "provider": { "@type": "SportsOrganization", "name": "İRÜ Spor Kulübü" },
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "onsite",
            "courseSchedule": { "@type": "Schedule" },
            "location": {
              "@type": "Place",
              "name": "İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi",
              "address": { "@type": "PostalAddress", "addressLocality": "İstanbul", "addressRegion": "Kadıköy", "addressCountry": "TR" }
            }
          },
          "audience": { "@type": "EducationalAudience", "educationalRole": "student", "requiredMinAge": 6, "requiredMaxAge": 14 }
        }}
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Basketbol" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/90 via-[#1A1A1A]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <Link to="/branslarimiz" className="inline-flex items-center gap-1 text-xs font-accent uppercase tracking-widest text-white/60 hover:text-white/80 transition-colors mb-4">
            {"Branşlarımız"} <span className="mx-1">/</span>
          </Link>
          <div className="flex items-center gap-5 mb-4">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white">
                Basketbol
              </h1>
              <p className="text-xs sm:text-sm font-accent uppercase tracking-widest text-gold mt-1">{"Profesyonel Eğitim Programı"}</p>
            </div>
          </div>
          <p className="mt-4 text-base md:text-lg text-white/70 max-w-lg leading-relaxed">
            {"Dinamik antrenmanlar, bireysel gelişim ve takım oyunu. Geleceğin basketbolcularını yetiştiriyoruz."}
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            {details.map((d) => (
              <div key={d.label} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2">
                <d.icon className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <div>
                  <span className="text-[10px] text-white/50 font-accent uppercase tracking-wider">{d.label}</span>
                  <p className="text-sm text-white/90 font-medium">{d.value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to="/on-kayit">
              <Button data-testid="bball-register-btn" className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-6">
                {"Kayıt Ol"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button data-testid="bball-info-btn" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-8 py-6">
                <Phone className="w-4 h-4 mr-2" /> Bilgi Al
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hakkında */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"Branş Detayı"}</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
                {"Ataşehir, Kadıköy, Maltepe Basketbol Kursu"}
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">
                {"İRÜ Spor Kulübü basketbol branşı, Ataşehir, Kadıköy ve Maltepe bölgesinden gelen çocuklarımıza bireysel yeteneklerin geliştirilmesinin yanı sıra, takım içi uyum ve liderlik becerilerinin kazandırılmasına odaklanmaktadır. Modern antrenman metodlarıyla geleceğin basketbolcularını yetiştiriyoruz."}
              </p>
              <p className="mt-4 text-gray-600 leading-relaxed">
                {"Deneyimli antrenör kadromuz, İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi'ndeki modern tesislerimizde her sporcunun bireysel potansiyelini keşfederek ona uygun gelişim planı hazırlamaktadır. Turnuvalarda müsabaka deneyimi kazanarak gerçek rekabet ortamında yetişmektedirler."}
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-burgundy mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-gray-600">{h}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={IMAGES.dribble} alt="Basketbol dribling" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden mt-8">
                <img src={IMAGES.smile} alt="Basketbol sporcu" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eğitim Seviyeleri */}
      <section className="py-20 lg:py-28 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"Eğitim Programı"}</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            {"Seviyeye Göre Programlar"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trainingProgram.map((p) => (
              <div key={p.level} className="border border-zinc-200 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 overflow-hidden bg-white">
                <div className={`${p.color} px-6 py-4 flex items-center justify-between`}>
                  <span className="font-heading text-base font-bold uppercase tracking-wider text-white">{p.level}</span>
                  <span className="font-accent text-xs text-white/80 bg-white/20 px-3 py-1">{p.ages}</span>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Galeri */}
      <section className="py-20 lg:py-28 bg-white" data-testid="basketball-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Galeri</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            Basketboldan Kareler
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
            {galleryItems.map((item) => (
              <div
                key={item.title}
                className={`relative group overflow-hidden cursor-pointer ${item.span || ""}`}
                onClick={() => setLightboxImg(item.img)}
                data-testid={`gallery-item-${item.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  <p className="font-heading text-xs uppercase tracking-wider text-white">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImg(null)}
          data-testid="gallery-lightbox"
        >
          <img src={lightboxImg} alt="Galeri" className="max-w-full max-h-[85vh] object-contain" />
          <button className="absolute top-6 right-6 text-white/70 hover:text-white text-3xl font-light">&times;</button>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-burgundy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">{"Basketbol Ailemize Katılın"}</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            {"Dinamik antrenmanlar ve takım oyunuyla çocuklarınızın basketbol yeteneklerini geliştirin."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/on-kayit">
              <Button data-testid="bball-cta-register" className="bg-white text-burgundy hover:bg-gray-100 font-heading uppercase tracking-wider text-sm px-10 py-6">
                {"Ön Kayıt Formu"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button data-testid="bball-cta-info" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-10 py-6">
                <Phone className="w-4 h-4 mr-2" /> Bilgi Al
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
